/**
 * RoadGraph — planar graph data structure for the city road network.
 *
 * Nodes represent intersections (or road endpoints), edges represent road
 * segments. An adjacency map keeps per-node edge lists for O(degree) traversal.
 *
 * Implements the `RoadGraph` interface from config.ts, plus the `degree()`
 * helper used by BlockExtractor and other downstream stages.
 */

import type { RoadGraph, RoadNode, RoadEdge } from '../config.js';

export class RoadGraphImpl implements RoadGraph {
  nodes: RoadNode[] = [];
  edges: RoadEdge[] = [];

  /** nodeId → list of edgeIds incident to that node */
  private nodeAdj = new Map<number, number[]>();

  // --------------------------------------------------------------------------
  // RoadGraph interface
  // --------------------------------------------------------------------------

  addNode(x: number, z: number): RoadNode {
    const id = this.nodes.length;
    const node: RoadNode = { id, x, z };
    this.nodes.push(node);
    this.nodeAdj.set(id, []);
    return node;
  }

  addEdge(
    fromId: number,
    toId: number,
    roadClass: RoadEdge['roadClass'],
    halfWidth: number,
  ): RoadEdge {
    const from = this.nodes[fromId];
    const to   = this.nodes[toId];

    const id = this.edges.length;
    const polyline = new Float64Array([from.x, from.z, to.x, to.z]);
    const edge: RoadEdge = {
      id,
      from: fromId,
      to:   toId,
      polyline,
      roadClass,
      halfWidth,
      gradeProfile: new Float32Array(0),
    };

    this.edges.push(edge);
    this.nodeAdj.get(fromId)!.push(id);
    this.nodeAdj.get(toId)!.push(id);

    return edge;
  }

  getEdgesFromNode(nodeId: number): RoadEdge[] {
    const ids = this.nodeAdj.get(nodeId) ?? [];
    return ids.map(i => this.edges[i]);
  }

  findNearestNode(x: number, z: number, maxDist: number): RoadNode | null {
    let best: RoadNode | null = null;
    let bestDistSq = maxDist * maxDist;

    for (const node of this.nodes) {
      const dx = node.x - x;
      const dz = node.z - z;
      const dSq = dx * dx + dz * dz;
      if (dSq < bestDistSq) {
        bestDistSq = dSq;
        best = node;
      }
    }

    return best;
  }

  // --------------------------------------------------------------------------
  // Extra helpers
  // --------------------------------------------------------------------------

  /** Number of edges incident to `nodeId`. */
  degree(nodeId: number): number {
    return (this.nodeAdj.get(nodeId) ?? []).length;
  }
}
